import React from 'react';
import { translateOuts } from '../utils/gameUtils';

const FONT_MAP = {
    serif: 'Georgia, "Times New Roman", serif',
    sans: '"Helvetica Neue", Arial, sans-serif',
    mono: '"SF Mono", "Courier New", monospace',
};

function ClassicLayout({ awayTeam, homeTeam, awayR, awayH, awayE, homeR, homeH, homeE, inningLabel, outsInHalf, isFinal, showRHE, showInning, showOuts }) {
    return (
        <>
            <div className="rhe-head">
                {showInning && (
                    <div className="inning-label">
                        {inningLabel}
                        {showOuts && !isFinal && (
                            <div className="out-dots">
                                {[0, 1].map(i => (
                                    <div key={i} className={`out-dot${i < outsInHalf ? ' out-dot-filled' : ''}`} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {showRHE && (
                    <>
                        <div className="col">R</div>
                        <div className="col">H</div>
                        <div className="col">E</div>
                    </>
                )}
                {!showRHE && <div className="col">R</div>}
            </div>
            <div className="score-row">
                <div className="sc-team">{awayTeam ?? 'Away'}</div>
                <div className="sc-r">{awayR ?? 0}</div>
                {showRHE && <div className="sc-h">{awayH ?? 0}</div>}
                {showRHE && <div className="sc-e">{awayE ?? 0}</div>}
            </div>
            <div className="score-row">
                <div className="sc-team">{homeTeam ?? 'Home'}</div>
                <div className="sc-r">{homeR ?? 0}</div>
                {showRHE && <div className="sc-h">{homeH ?? 0}</div>}
                {showRHE && <div className="sc-e">{homeE ?? 0}</div>}
            </div>
        </>
    );
}

function VerticalLayout({ awayTeam, homeTeam, awayR, awayH, awayE, homeR, homeH, homeE, inningLabel, outsInHalf, isFinal, showRHE, showInning, showOuts }) {
    return (
        <>
            {showInning && <div className="inning-label">{inningLabel}</div>}
            <div className="vertical-team-row">
                <span className="sc-team">{awayTeam ?? 'Away'}</span>
                <span className="sc-r">{awayR ?? 0}</span>
                {showRHE && <span className="sc-h">{awayH ?? 0}</span>}
                {showRHE && <span className="sc-e">{awayE ?? 0}</span>}
            </div>
            <div className="vertical-team-row">
                <span className="sc-team">{homeTeam ?? 'Home'}</span>
                <span className="sc-r">{homeR ?? 0}</span>
                {showRHE && <span className="sc-h">{homeH ?? 0}</span>}
                {showRHE && <span className="sc-e">{homeE ?? 0}</span>}
            </div>
            {showOuts && !isFinal && (
                <div className="out-dots">
                    {[0, 1].map(i => (
                        <div key={i} className={`out-dot${i < outsInHalf ? ' out-dot-filled' : ''}`} />
                    ))}
                </div>
            )}
        </>
    );
}

function MinimalLayout({ awayTeam, homeTeam, awayR, homeR, inningLabel, showInning }) {
    return (
        <div className="minimal-score">
            <span className="sc-team">{awayTeam ?? 'Away'}</span>
            <span className="minimal-score-value">{awayR ?? 0}</span>
            <span className="minimal-divider">-</span>
            <span className="minimal-score-value">{homeR ?? 0}</span>
            <span className="sc-team">{homeTeam ?? 'Home'}</span>
            {showInning && <div className="minimal-inning">{inningLabel}</div>}
        </div>
    );
}

function CompactScoreboard({ awayTeam, homeTeam, awayR, awayH, awayE, homeR, homeH, homeE, ipOuts, isFinal, scoreboardLayout }) {
    const { inning, inningHalf } = translateOuts(ipOuts ?? 0, isFinal);
    const halfArrow = inningHalf === 'Top' ? '▲' : '▼';
    const inningLabel = isFinal ? 'Final' : `${halfArrow} ${inning}`;
    const outsInHalf = (ipOuts ?? 0) % 3;

    const style = scoreboardLayout?.style ?? 'classic';
    const showRHE = scoreboardLayout?.showRHE !== false;
    const showInning = scoreboardLayout?.showInning !== false;
    const showOuts = scoreboardLayout?.showOuts !== false;

    const fontStyle = scoreboardLayout?.fontFamily
        ? { fontFamily: FONT_MAP[scoreboardLayout.fontFamily] || undefined }
        : undefined;

    const sizeClass = scoreboardLayout?.fontSize
        ? ` compact-scoreboard--${scoreboardLayout.fontSize}`
        : '';
    const imageOverlay = scoreboardLayout?.imageOverlay ? ' compact-scoreboard--image-overlay' : '';

    const layoutProps = { awayTeam, homeTeam, awayR, awayH, awayE, homeR, homeH, homeE, inningLabel, outsInHalf, isFinal, showRHE, showInning, showOuts };

    return (
        <div className={`compact-scoreboard compact-scoreboard--${style}${sizeClass}${imageOverlay}`} style={fontStyle}>
            {style === 'vertical' && <VerticalLayout {...layoutProps} />}
            {style === 'minimal' && <MinimalLayout {...layoutProps} />}
            {style !== 'vertical' && style !== 'minimal' && <ClassicLayout {...layoutProps} />}
        </div>
    );
}

export default CompactScoreboard;
